import { Global } from '@emotion/react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { memo, useMemo, useState } from 'react'

import type { ThemeName } from '../foundations/pharaoh-theme-context.ts'
import { PharaohThemeProvider } from '../foundations/pharaoh-theme-provider.tsx'
import { type ButtonClickContext, TrackButtonClickContext } from '../wrapper-context.tsx'

import type { CoBranding } from './co-branding/co-branding.ts'
import { CoBrandingContextProvider } from './co-branding/hooks.ts'
import { setupLicenses } from './licenses.ts'
import { DefaultLanguageProviderWrapper } from './localization/index.ts'
import { theme } from './theme.ts'

export type PharaohWrapperProps = {
  children?: React.ReactNode
  clickButtonCallback: (clickContext: ButtonClickContext) => void
  /**
   * Initial pharaoh design-system theme. Defaults to `'light'`.
   * The active theme can also be changed at runtime via {@link usePharaohTheme}.
   */
  pharaohTheme?: ThemeName
}

export const PharaohWrapper = memo<PharaohWrapperProps>(function PharaohWrapper({
  children,
  clickButtonCallback,
  pharaohTheme,
}) {
  setupLicenses()

  const [coBranding, setCoBranding] = useState<CoBranding | null>(null)

  const coBrandingContextData = useMemo(() => ({ coBranding, setCoBranding }), [coBranding, setCoBranding])

  coBranding?.additionalStylesheets.forEach(ensureStylesheetAdded)

  return (
    <TrackButtonClickContext.Provider value={clickButtonCallback}>
      <PharaohThemeProvider theme={pharaohTheme ?? 'light'}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {coBranding?.additionalStyles ? <Global styles={coBranding.additionalStyles} /> : null}
          <CoBrandingContextProvider value={coBrandingContextData}>
            <DefaultLanguageProviderWrapper>{children}</DefaultLanguageProviderWrapper>
          </CoBrandingContextProvider>
        </ThemeProvider>
      </PharaohThemeProvider>
    </TrackButtonClickContext.Provider>
  )
})

const addedStylesheets = new Set<string>()
function ensureStylesheetAdded(url: string) {
  if (addedStylesheets.has(url)) return

  const link = document.createElement('link')

  link.type = 'text/css'
  link.rel = 'stylesheet'
  link.href = url

  document.head.appendChild(link)

  addedStylesheets.add(url)
}
